import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckIcon, PlusIcon } from "@heroicons/react/outline";
import { SearchInput } from "src/common/components/SearchInput";
import type { RagDocument } from "../api/ragApi";
import { formatRagSize } from "../formatRagSize";
import { TruncatedText } from "../../../common/components/TruncatedText/TruncatedText";

const statusConfig: Record<string, { dot: string }> = {
  processing: { dot: "bg-yellow-400 animate-pulse" },
  ready: { dot: "bg-green-500" },
  error: { dot: "bg-red-500" },
};

export type DocumentLibraryListMode =
  | {
      kind: "attach";
      attachedIds: number[];
      attachingId: number | null;
      onAttach: (docId: number) => void;
    }
  | { kind: "select"; selectedIds: number[]; onToggle: (docId: number) => void };

interface DocumentLibraryListProps {
  docs: RagDocument[];
  loading: boolean;
  mode: DocumentLibraryListMode;
}

const DocumentLibraryList = ({ docs, loading, mode }: DocumentLibraryListProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");

  const attachedIds = mode.kind === "attach" ? mode.attachedIds : null;

  const filteredDocs = useMemo(() => {
    const q = search.toLowerCase().trim();
    const matching = q ? docs.filter((d) => d.filename.toLowerCase().includes(q)) : docs;
    if (!attachedIds) return matching;
    return [...matching].sort((a, b) => {
      const aAttached = attachedIds.includes(a.id) ? 1 : 0;
      const bAttached = attachedIds.includes(b.id) ? 1 : 0;
      return aAttached - bAttached;
    });
  }, [docs, search, attachedIds]);

  const renderDocInfo = (doc: RagDocument) => {
    const cfg = statusConfig[doc.status] || statusConfig.error;
    return (
      <>
        <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
        <div className="flex-1 min-w-0">
          <TruncatedText className="text-gray-900 font-medium text-sm" text={doc.filename} />
          <span className="text-xs text-gray-400">
            {formatRagSize(doc.size_bytes)}
            {doc.chunk_count > 0 && ` · ${doc.chunk_count} chunks`}
          </span>
        </div>
      </>
    );
  };

  const renderAttachRow = (
    doc: RagDocument,
    attachMode: Extract<DocumentLibraryListMode, { kind: "attach" }>,
  ) => {
    const isAttached = attachMode.attachedIds.includes(doc.id);
    const isAttaching = attachMode.attachingId === doc.id;
    return (
      <div
        key={doc.id}
        className={`flex items-center gap-3 px-3 py-2 rounded-lg border text-sm ${
          isAttached ? "border-gray-200 bg-gray-50 opacity-60" : "border-gray-200 bg-white"
        }`}
      >
        {renderDocInfo(doc)}
        {isAttached ? (
          <span className="text-xs text-gray-400 flex-shrink-0">
            {t("pages.agents.alreadyAttached")}
          </span>
        ) : (
          <button
            onClick={() => attachMode.onAttach(doc.id)}
            disabled={isAttaching || doc.status === "error"}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg transition-colors disabled:opacity-50 flex-shrink-0"
          >
            {isAttaching ? (
              <span className="w-3 h-3 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
            ) : (
              <PlusIcon className="h-3 w-3" strokeWidth={2} />
            )}
            {t("pages.agents.attach")}
          </button>
        )}
      </div>
    );
  };

  const renderSelectRow = (
    doc: RagDocument,
    selectMode: Extract<DocumentLibraryListMode, { kind: "select" }>,
  ) => {
    const isSelected = selectMode.selectedIds.includes(doc.id);
    const isDisabled = doc.status === "error";
    return (
      <button
        key={doc.id}
        type="button"
        onClick={() => selectMode.onToggle(doc.id)}
        disabled={isDisabled}
        className={`flex items-center gap-3 px-3 py-2 rounded-lg border text-sm text-left w-full transition-all ${
          isSelected
            ? "border-green-400 bg-green-50 shadow-sm"
            : "border-gray-200 bg-white hover:border-gray-300"
        } ${isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
      >
        {renderDocInfo(doc)}
        <div
          className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
            isSelected ? "border-green-500 bg-green-500" : "border-gray-300"
          }`}
        >
          {isSelected && <CheckIcon className="w-3 h-3 text-white" strokeWidth={3} />}
        </div>
      </button>
    );
  };

  return (
    <>
      {!loading && docs.length > 0 && (
        <div className="mb-2">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={t("general.search") + "..."}
          />
          <p className="text-xs text-gray-400 mt-1.5 tabular-nums">
            {filteredDocs.length} / {docs.length}
            {search.trim() && ` - "${search.trim()}"`}
          </p>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-6">
          <div className="w-6 h-6 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : docs.length === 0 ? (
        <p className="text-sm text-gray-400 py-6 text-center">
          {t("pages.agents.noLibraryDocuments")}
        </p>
      ) : filteredDocs.length === 0 ? (
        <p className="text-sm text-gray-400 py-6 text-center">
          {t("statsNew.emptyState.noSearchResults")}
        </p>
      ) : (
        <div className="flex flex-col gap-1.5 max-h-[380px] overflow-y-auto">
          {filteredDocs.map((doc) =>
            mode.kind === "attach" ? renderAttachRow(doc, mode) : renderSelectRow(doc, mode),
          )}
        </div>
      )}
    </>
  );
};

export default DocumentLibraryList;
